"use client";

import Link from "next/link";
import { Suspense, useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { check_auth, getDemoCourses, signupDemoStudent } from "@/api";

const getCourseId = (course) => course?.id || course?._id || "";

function RegisterPageContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone_number: "",
    password: "",
    course_id: "",
  });

  useEffect(() => {
    const init = async () => {
      try {
        const session = await check_auth();
        if (session) {
          router.replace("/dashboard");
          return;
        }
      } catch {
        // ignore
      } finally {
        setCheckingAuth(false);
      }

      try {
        const demoCourses = await getDemoCourses();
        setCourses(Array.isArray(demoCourses) ? demoCourses : []);
      } catch (error) {
        setStatus({
          type: "error",
          message: "Could not load demo courses right now.",
        });
      } finally {
        setLoadingCourses(false);
      }
    };

    init();
  }, [router]);

  useEffect(() => {
    if (loadingCourses) return;

    const email = searchParams.get("email") || "";
    const name = searchParams.get("name") || "";
    const phoneNumber = searchParams.get("phone_number") || "";
    const requestedCourseId = searchParams.get("course_id") || "";
    const requestedCourseTitle = searchParams.get("course_title") || "";

    const matchedCourse =
      courses.find((course) => String(getCourseId(course)) === String(requestedCourseId)) ||
      courses.find(
        (course) =>
          course?.title &&
          requestedCourseTitle &&
          String(course.title).trim().toLowerCase() === String(requestedCourseTitle).trim().toLowerCase()
      );

    setForm((prev) => ({
      ...prev,
      name: name || prev.name,
      email: email || prev.email,
      phone_number: phoneNumber || prev.phone_number,
      course_id: matchedCourse ? getCourseId(matchedCourse) : prev.course_id,
    }));
  }, [courses, loadingCourses, searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: "", message: "" });
    setSubmitting(true);

    try {
      const result = await signupDemoStudent(form);
      router.replace(result?.redirect_to || `/learn/${form.course_id}`);
    } catch (error) {
      setStatus({
        type: "error",
        message:
          error?.response?.data?.error || "Could not create your demo account.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (checkingAuth) return null;

  return (
    <section className="min-h-screen bg-[radial-gradient(circle_at_top_left,#eef7ff,transparent_38%),linear-gradient(160deg,#ffffff_0%,#f8fbff_55%,#edf4ff_100%)] px-4 py-10">
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 lg:grid-cols-[0.9fr_1.1fr]">
        <div className="rounded-[28px] border border-[#d8e7ff] bg-white p-8 shadow-[0_20px_60px_rgba(10,45,90,0.08)]">
          <p className="text-sm font-medium uppercase tracking-[0.18em] text-[#0d6fea]">
            Demo Registration
          </p>
          <h1 className="mt-3 text-3xl font-semibold text-[#1a1a1a]">
            Create your account and enter a demo course directly
          </h1>
          <p className="mt-4 text-base leading-7 text-gray-600">
            Use your email, phone number, and password to create a demo account.
            Once registration is successful, you will be signed in and redirected
            to the demo course you selected.
          </p>

          <form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-4">
            <input
              type="text"
              placeholder="Full name"
              className="w-full rounded-2xl border border-[#d5dded] px-4 py-3 outline-none transition focus:border-[#0d6fea]"
              value={form.name}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, name: e.target.value }))
              }
              required
            />
            <input
              type="email"
              placeholder="Email address"
              className="w-full rounded-2xl border border-[#d5dded] px-4 py-3 outline-none transition focus:border-[#0d6fea]"
              value={form.email}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, email: e.target.value }))
              }
              required
            />
            <input
              type="tel"
              placeholder="Phone number"
              className="w-full rounded-2xl border border-[#d5dded] px-4 py-3 outline-none transition focus:border-[#0d6fea]"
              value={form.phone_number}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, phone_number: e.target.value }))
              }
              required
            />
            <input
              type="password"
              placeholder="Password"
              minLength={8}
              className="w-full rounded-2xl border border-[#d5dded] px-4 py-3 outline-none transition focus:border-[#0d6fea]"
              value={form.password}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, password: e.target.value }))
              }
              required
            />
            <select
              className="w-full rounded-2xl border border-[#d5dded] px-4 py-3 outline-none transition focus:border-[#0d6fea] bg-white"
              value={form.course_id}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, course_id: e.target.value }))
              }
              required
              disabled={loadingCourses}
            >
              <option value="">
                {loadingCourses ? "Loading demo courses..." : "Select a demo course"}
              </option>
              {courses.map((course) => (
                <option key={getCourseId(course)} value={getCourseId(course)}>
                  {course?.title}
                </option>
              ))}
            </select>

            {status.message ? (
              <p
                className={`text-sm ${
                  status.type === "success" ? "text-green-600" : "text-red-600"
                }`}
              >
                {status.message}
              </p>
            ) : null}

            <button
              type="submit"
              disabled={submitting || loadingCourses}
              className="rounded-2xl bg-[#0d6fea] px-4 py-3 font-medium text-white disabled:opacity-60"
            >
              {submitting ? "Creating account..." : "Create Demo Account"}
            </button>
          </form>

          <div className="mt-5 rounded-2xl border border-[#d8e7ff] bg-[#f7fbff] px-4 py-4">
            <p className="text-sm font-medium text-[#1a1a1a]">
              Want full access instead of a demo account?
            </p>
            <Link
              href="/register/full"
              className="mt-2 inline-flex text-sm font-medium text-[#0d6fea]"
            >
              Switch to full course signup
            </Link>
          </div>

          <div className="mt-5 flex flex-wrap items-center justify-between gap-3 text-sm">
            <Link href="/login" className="text-[#0d6fea]">
              Already have an account? Login
            </Link>
          </div>
        </div>

        <div className="rounded-[28px] bg-[#0a2f5e] p-8 text-white shadow-[0_28px_80px_rgba(8,36,72,0.22)]">
          <p className="text-sm uppercase tracking-[0.24em] text-blue-200">
            Available Demo Courses
          </p>
          <div className="mt-6 grid gap-4">
            {loadingCourses ? (
              <div className="rounded-3xl border border-white/10 bg-white/10 p-5 text-sm text-blue-100">
                Loading demo courses...
              </div>
            ) : courses.length === 0 ? (
              <div className="rounded-3xl border border-white/10 bg-white/10 p-5 text-sm text-blue-100">
                No demo courses are available right now.
              </div>
            ) : (
              courses.map((course) => (
                <button
                  key={getCourseId(course)}
                  type="button"
                  onClick={() =>
                    setForm((prev) => ({
                      ...prev,
                      course_id: getCourseId(course),
                    }))
                  }
                  className={`rounded-3xl border p-5 text-left transition ${
                    form.course_id === getCourseId(course)
                      ? "border-white bg-white text-[#0a2f5e]"
                      : "border-white/15 bg-white/10 text-white hover:bg-white/15"
                  }`}
                >
                  <p className="text-xl font-semibold">{course?.title}</p>
                  <p
                    className={`mt-2 text-sm leading-6 ${
                      form.course_id === getCourseId(course)
                        ? "text-[#375177]"
                        : "text-blue-100"
                    }`}
                  >
                    {course?.short_description || course?.description || "Demo course access"}
                  </p>
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function RegisterPage() {
  return (
    <Suspense fallback={null}>
      <RegisterPageContent />
    </Suspense>
  );
}
